// UserContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  Account,
  Databases,
  ID,
  Query,
  Storage,
  OAuthProvider,
} from "appwrite";
import client from "./appwriteConfig";
import { getToken } from "firebase/messaging";
import { messaging } from "./firebaseConfig";
import { toast } from "react-toastify";

const UserContext = createContext();
export const useUser = () => useContext(UserContext);
const DATABASE_CARTID = process.env.REACT_APP_DATABASE_ID;
const COLLECTION_CARTID = process.env.REACT_APP_COLLECTION_CART_ID;

export const UserProvider = ({ children }) => {
  const account = new Account(client);
  const storage = new Storage(client);
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [userPlan, setUserPlan] = useState("trial");
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);

  const [cartLoading, setCartLoading] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const togglePlay = () => {
    setIsPlaying((prev) => !prev);
  };
  // Start playing a track from a given list (ssqueue)
  const startPlaylist = (list, track) => {
    const safeList = Array.isArray(list) ? list : [];
    const idx = safeList.findIndex((t) => t?.$id === track?.$id);

    setPlaylist(safeList);
    setCurrentTrack(track);
    setCurrentTrackIndex(idx);

    // Force play (don’t rely on toggle)
    setIsPlaying(true);
  };

  const playNextTrack = () => {
    if (!playlist.length) return;

    const nextIndex =
      currentTrackIndex >= 0 ? (currentTrackIndex + 1) % playlist.length : 0;

    setCurrentTrackIndex(nextIndex);
    setCurrentTrack(playlist[nextIndex]);
    setIsPlaying(true);
  };

  const playPrevTrack = () => {
    if (!playlist.length) return;

    const prevIndex =
      currentTrackIndex >= 0
        ? (currentTrackIndex - 1 + playlist.length) % playlist.length
        : 0;

    setCurrentTrackIndex(prevIndex);
    setCurrentTrack(playlist[prevIndex]);
    setIsPlaying(true);
  };
  const stopCurrentTrack = () => {
    setIsPlaying(false);
    setCurrentTrack(null);
    setCurrentTrackIndex(-1);
  };
  const getFCMToken = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.warn("🔒 Notification permission not granted:", permission);
        return null;
      }

      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
      });

      if (token) {
        // console.log("✅ Token retrieved:", token);
        return token;
      } else {
        console.warn("⚠️ getToken returned null");
        return null;
      }
    } catch (err) {
      console.error("❌ getFCMToken failed:", err);
      return null;
    }
  };

  const saveFCMToken = async (newToken) => {
    try {
      const currentPrefs = await account.getPrefs();
      let existingTokens = [];

      // Handle stringified token array
      if (typeof currentPrefs.fcmTokens === "string") {
        try {
          const parsed = JSON.parse(currentPrefs.fcmTokens);
          if (Array.isArray(parsed)) {
            existingTokens = parsed;
          }
        } catch (err) {
          console.warn("⚠️ fcmTokens was not valid JSON, resetting to []");
          existingTokens = [];
        }
      }

      // Avoid duplicate tokens
      if (!existingTokens.includes(newToken)) {
        existingTokens.push(newToken);
        const updatedPrefs = {
          ...currentPrefs,
          fcmTokens: JSON.stringify(existingTokens),
        };

        await account.updatePrefs(updatedPrefs);
      }
    } catch (err) {
      console.error("❌ Failed to save FCM token:", err.message);
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const userData = await account.get();

        setUser(userData);

        try {
          const prefs = await account.getPrefs();
          setUserPlan(prefs.subscriptionPlan || "trial");
          setTermsAccepted(prefs.termsAccepted === true);
        } catch (prefsError) {
          console.warn("Could not load user prefs:", prefsError.message);
          setUserPlan("trial");
        }

        try {
          const token = await getFCMToken();
          if (token) {
            await saveFCMToken(token);
          }
        } catch (fcmError) {
          console.warn("FCM skipped:", fcmError.message);
        }
      } catch (error) {
        console.log("No active Appwrite session");
        setUser(null);
        setUserPlan("trial");
        setCart([]);
      }
    };

    checkSession();
  }, []);

  useEffect(() => {
    fetchCartData();
  }, [user]);
  const getJWT = async () => {
    const jwt = await account.createJWT();
    return jwt.jwt;
  };
  const login = async (email, password, onLoginSuccess = () => {}) => {
    try {
      await account.createEmailPasswordSession(email, password);
      const userData = await account.get();
      setUser(userData);
      const prefs = await account.getPrefs();
      setUserPlan(prefs.subscriptionPlan || "trial");
      console.log(userData);
      const token = await getFCMToken();
      // console.log(token);
      await saveFCMToken(token);

      onLoginSuccess();
    } catch (error) {
      throw new Error(
        "Invalid credentials. Please check the email and password.",
      );
    }
  };
  const refreshUserPlan = async () => {
    try {
      const prefs = await account.getPrefs();
      setUserPlan(prefs.subscriptionPlan || "trial");

      const freshUser = await account.get();
      setUser(freshUser);
    } catch (error) {
      console.error("Failed to refresh user plan:", error);
    }
  };
  const loginWithGoogle = async () => {
    try {
      await account.createOAuth2Session(
        OAuthProvider.Google, // Provider
        window.location.origin, // Redirect on success
        window.location.origin, // Redirect on failure
      );

      // Wait for the user to be redirected back and then fetch user details
      setTimeout(async () => {
        const userData = await account.get(); // Fetch user data
        setUser(userData);
        console.log("User Info:", userData);
        const token = await getFCMToken();
        await saveFCMToken(token);
      }, 3000); // Small delay to ensure session is established
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await account.deleteSession("current");
      setUser(null);
      setCart([]); // Clear cart on logout
      setUserPlan("trial");
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  };

  const register = async (
    name,
    email,
    password,
    onRegistrationSuccess = () => {},
  ) => {
    try {
      await account.create(ID.unique(), email, password, name);
      await login(email, password, onRegistrationSuccess);
    } catch (error) {
      console.error("Registration failed:", error.message);
      throw new Error(error.message);
    }
  };

  const loginWithPhone = async (phone, otp, onLoginSuccess = () => {}) => {
    try {
      // Step 1: Check if a token already exists for the phone number
      let token = localStorage.getItem(`phoneToken_${phone}`);

      // If no token is found, create a new one
      if (!token) {
        token = await account.createPhoneToken(ID.unique(), phone);
        localStorage.setItem(`phoneToken_${phone}`, JSON.stringify(token));
      } else {
        token = JSON.parse(token);
      }

      if (otp) {
        // Step 2: Use the OTP to create a session
        await account.createSession(token.userId, otp);
        const userData = await account.get();
        console.log("User after phone login:", userData);
        setUser(userData); // Set the user data with updated name
        localStorage.removeItem(`phoneToken_${phone}`); // Clear the token after successful login
        try {
          const deviceToken = await getFCMToken();
          await saveFCMToken(deviceToken); // ← Call this helper
        } catch (fcmError) {
          console.warn("Failed to get/save FCM token:", fcmError);
        }
        onLoginSuccess();
      }
    } catch (error) {
      localStorage.removeItem(`phoneToken_${phone}`); // Clear the token if there's an error
      throw new Error("Failed to login with phone. Please check the OTP.");
    }
  };
  const updateUserPlan = async (planId) => {
    const currentPrefs = await account.getPrefs();

    await account.updatePrefs({
      ...currentPrefs,
      subscriptionPlan: planId,
      subscriptionStatus: planId === "trial" ? "inactive" : "active",
      beatsRemaining: planId === "producer" ? 3 : planId === "creator" ? 1 : 0,
      subscriptionUpdatedAt: new Date().toISOString(),
    });

    setUserPlan(planId);
  };
  const registerWithPhone = async (
    phone,
    otp = "",
    name = "",
    onRegistrationSuccess = () => {},
  ) => {
    try {
      // Check if a token already exists for the phone number
      let token = localStorage.getItem(`phoneToken_${phone}`);

      if (!token) {
        // Create a new token if it doesn't exist
        token = await account.createPhoneToken(ID.unique(), phone);
        localStorage.setItem(`phoneToken_${phone}`, JSON.stringify(token));
      } else {
        token = JSON.parse(token);
      }

      if (otp) {
        // Use the OTP to create a session
        await account.createSession(token.userId, otp);
        const userData = await account.get();

        // Update user's name after OTP verification
        if (name) {
          await account.updateName(name);
          userData.name = name; // Update local user data with new name
        }

        setUser(userData); // Set the user data with updated name
        localStorage.removeItem(`phoneToken_${phone}`); // Clear token after registration
        onRegistrationSuccess();
      }
    } catch (error) {
      localStorage.removeItem(`phoneToken_${phone}`); // Clear token if there's an error
      throw new Error("Failed to register with phone. Please check the OTP.");
    }
  };
  const fetchCartData = async () => {
    const database = new Databases(client, "default");
    if (user) {
      try {
        const { documents } = await database.listDocuments(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_CART_ID,
          [Query.equal("userId", user.$id)],
        );
        setCart(documents);
      } catch (error) {
        console.error("Failed to fetch cart data:", error);
      }
    }
  };

  const addToCart = async (product, quantity = 1) => {
    if (!user) {
      toast.error("⚠️ Please log in to add items to your cart.");
      return;
    }

    setCartLoading((prev) => ({ ...prev, [product.$id]: true })); // Start loading

    let totalCost = product.cost * quantity; // Calculate total cost
    let cartData = {
      userId: user.$id,
      email: user.email ? user.email : null, // Save email as null if not available
      mobileNumber: user.phone ? user.phone : null, // Save phone as null if not available

      name: user.name,
      productId: product.$id,
      productName:
        product.beatName || product.tutorialTitle || product.itemName,
      cost: product.cost,
      productType: product.productType,
      quantity: quantity,
      totalCost: totalCost, // Add total cost
      sellerEmail: product.email,
    };

    if (product.productType === "beat") {
      const beatInfo = JSON.stringify({
        beatId: product.$id,
        beatName: product.beatName,
        artistName: product.artistName,
        cost: product.cost,

        sellerEmail: product.email,
        // ✅ Add this line
        // email: product.email,
      });

      cartData = {
        ...cartData,
        tier: product.tier || "basic",
        imageUrl: product.imageUrl,
        BEATinfo: beatInfo, // Store the consolidated beat information
      };
    } else if (product.productType === "fashion") {
      const fashionInfo = JSON.stringify({
        fashionId: product.$id,
        fashionUrl: product.fashionUrl,
        cost: product.cost,
        quantity: quantity,

        sellerEmail: product.email,
        // email: product.email,
      });

      cartData = {
        ...cartData,
        fashionUrl: product.fashionUrl,
        FASHIONinfo: fashionInfo, // Store the consolidated fashion information
      };
    } else if (product.productType === "tutorial") {
      const tutorialInfo = JSON.stringify({
        tutorialId: product.$id,
        tutorialTitle: product.tutorialTitle,
        // videoUrl: product.videoUrl,
        authorName: product.authorName,

        cost: product.cost,
        sellerEmail: product.email,
      });

      cartData = {
        ...cartData,
        thumbnailUrl: product.thumbnailUrl,
        TUTORIALinfo: tutorialInfo, // Store the consolidated tutorial information
      };
    }

    try {
      const addedCart = new Databases(client, "default");
      await addedCart.createDocument(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_CART_ID,
        ID.unique(),
        cartData,
      );
      fetchCartData();
    } catch (error) {
      console.error("Failed to add to cart in database:", error);
    } finally {
      setCartLoading((prev) => ({ ...prev, [product.$id]: false })); // End loading
    }
  };

  const updateCartItem = async (productId, quantity) => {
    setCartLoading((prev) => ({ ...prev, [productId]: true })); // Start loading
    const totalCost =
      cart.find((item) => item.productId === productId).cost * quantity;
    try {
      const database = new Databases(client, "default");
      const response = await database.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_CART_ID,
        [Query.equal("productId", productId)],
      );
      if (response.documents.length > 0) {
        const documentId = response.documents[0].$id;
        await database.updateDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_CART_ID,
          documentId,
          {
            quantity: quantity,
            totalCost: totalCost,
          },
        );
        fetchCartData();
      }
    } catch (error) {
      console.error("Failed to update cart item in the database:", error);
    } finally {
      setCartLoading((prev) => ({ ...prev, [productId]: false })); // End loading
    }
  };

  const removeFromCart = async (productId) => {
    setCartLoading((prev) => ({ ...prev, [productId]: true })); // Start loading
    try {
      const database = new Databases(client, "default");
      const response = await database.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_CART_ID,
        [Query.equal("productId", productId)],
      );
      if (response.documents.length > 0) {
        const documentId = response.documents[0].$id;
        await database.deleteDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_CART_ID,
          documentId,
        );
        fetchCartData();
      }
    } catch (error) {
      console.error("Failed to remove item from the database:", error);
    } finally {
      setCartLoading((prev) => ({ ...prev, [productId]: false })); // End loading
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        storage,
        currentTrack,
        isPlaying,
        termsAccepted,
        setTermsAccepted,
        login,
        logout,
        getJWT,
        loginWithGoogle,
        cart,
        userPlan,
        updateUserPlan,
        refreshUserPlan,
        register,
        cartLoading,
        addToCart,
        playlist,
        currentTrackIndex,
        startPlaylist,
        playNextTrack,
        playPrevTrack,
        removeFromCart,
        registerWithPhone,
        fetchCartData,
        updateCartItem,
        setUser,
        setCurrentTrack,
        loginWithPhone,
        setIsPlaying,
        togglePlay,
        stopCurrentTrack,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
